import PouchDB from 'pouchdb';
import { loggingService } from '@/services/LoggingService';

/**
 * Interface representing an item stored in the memory cache
 */
interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Basic document structure for PouchDB documents
 */
interface PouchDocument {
  _id: string;
  _rev?: string;
  data?: unknown;
  timestamp?: string;
  [key: string]: unknown;
}

/**
 * Custom type for PouchDB document with data property
 * This extends the standard PouchDB document type to include our custom data field
 */
export interface PouchDocumentWithData extends PouchDB.Core.IdMeta, PouchDB.Core.GetMeta {
  data: any;
}

export class DatabaseService {
  private db: PouchDB.Database | null = null;
  private initialized = false;
  
  /**
   * Returns whether the database service has been initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
  
  private memoryCache: Map<string, CacheItem<unknown>> = new Map();
  private readonly CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  /**
   * Initializes the database connection with improved persistence across page navigations
   * Uses localStorage to track initialization state and avoid redundant initializations
   * 
   * @returns Promise that resolves when the database is initialized
   */
  async initialize(): Promise<void> {
    // Check if already initialized in memory
    if (this.initialized) return;
    
    // Check localStorage to avoid reinitialization across page loads
    const hasInitializedBefore = localStorage.getItem('db_initialized');
    if (hasInitializedBefore === 'true') {
      
      this.initialized = true;
      
      // Still need to set up the database connection
      if (!this.db) {
        this.db = new PouchDB('meduler_db', {
          auto_compaction: false
        } as PouchDB.Configuration.DatabaseConfiguration);
      }
      
      // Quick verification of database connection
      try {
        const info = await this.db.info();
        // Removed complex console statement for security;
        
        // Debug: Check for medications in the database
        this.debugLogMedications();
        
        return;
      } catch (error) {
        
        localStorage.removeItem('db_initialized'); // Clear flag to allow fresh initialization
      }
    }
    
    try {
      // Use a local PouchDB instance with improved configuration
      this.db = new PouchDB('meduler_db', {
        // Prevent auto compaction to reduce conflicts during development
        auto_compaction: false
      } as PouchDB.Configuration.DatabaseConfiguration);
      
      // Set additional options for Ajax operations (must be done after initialization)
      // This fixes TypeScript errors while still providing better reliability
      
      // Explicitly check database connection by calling info()
      try {
        await this.db.info();
      } catch (infoError) {
        
        // Try to recover by recreating the database
        try {
          
          await this.db.destroy();
          this.db = new PouchDB('meduler_db');
          await this.db.info();
        } catch (recreateError) {
          throw new Error(`Database recovery failed: ${recreateError.message}`);
        }
      }
      
      // Initialize database
      this.initialized = true;
      
      await this.ensureUserAnalyticsDocument();
      
      // Debug: Check for medications in the database
      // Use a proper binding to ensure 'this' context is preserved
      try {
        await this.debugLogMedications();
      } catch (error) {
        
        // Continue initialization despite logging errors
      }
      
      // Mark as initialized in localStorage
      localStorage.setItem('db_initialized', 'true');
    } catch (error) {
      this.initialized = false;
      
      // Create a fallback in-memory database when PouchDB fails
      
      throw error;
    }
  }

  private isValidCacheItem<T>(item: CacheItem<T>): boolean {
    return Date.now() - item.timestamp < item.ttl;
  }
  


  /**
   * Debug helper to log all medication data in the database
   * This helps trace medication persistence issues across page navigations
   * and verify data is being properly saved and retrieved
   */
  public async debugLogMedications(): Promise<void> {
    if (!this.db) {
      
      return;
    }
    
    try {
      // Log medications for both profile 'u1' and 'u2'
      const profiles = ['u1', 'u2'];
      
      
      
      for (const profileId of profiles) {
        try {
          const docId = `medications:${profileId}`;
          // Use type assertion to tell TypeScript about our custom document structure
          const doc = await this.db.get(docId) as PouchDocumentWithData;
          
          if (!doc || !doc.data) {
            
            continue;
          }
          
          // Check if data is encrypted
          if (typeof doc.data === 'object' && doc.data.isEncrypted) {
            // Removed complex console statement for security;
          } else {
            // For unencrypted data, we can log more details
            const data = doc.data as Record<string, any>;
            const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const medicationCounts = weekDays.map(day => {
              const meds = Array.isArray(data[day]) ? data[day] : [];
              return `${day}: ${meds.length}`;
            }).join(', ');
            
            // Removed complex console statement for security;
          }
        } catch (error) {
          if (error.status === 404) {
            
          } else {
            
          }
        }
      }
    } catch (error) {
      
    }
  }
  
  /**
   * Merges two objects deeply, combining their properties
   * Used for combining analytics data during conflict resolution
   *
   * @param target - Target object to merge into
   * @param source - Source object with properties to merge
   * @returns The merged object
   */
  /**
   * Merges two objects deeply, combining their properties
   * Used for combining analytics data during conflict resolution
   *
   * @param target - Target object to merge into
   * @param source - Source object with properties to merge
   * @returns The merged object
   */
  mergeObjects(target: unknown, source: unknown): unknown {
    // Handle base cases
    if (!target || typeof target !== 'object') return source;
    if (!source || typeof source !== 'object') return target;
    
    // Make a copy of the target to avoid mutations
    const result = { ...target as Record<string, unknown> };
    
    try {
      // Iterate through properties of source object
      Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
        // Skip null or undefined values from source
        if (value === null || value === undefined) {
          return;
        }
        
        // Handle arrays specially - concatenate them
        if (Array.isArray(value) && Array.isArray(result[key])) {
          // When both are arrays, concatenate unique items
          const targetArray = result[key] as unknown[];
          const merged = [...targetArray];
          
          // Only add items that don't already exist (for primitives)
          (value as unknown[]).forEach(item => {
            // Skip null or undefined array items
            if (item === null || item === undefined) {
              return;
            }
            
            // For objects in arrays, we can't easily check equality
            // so we just append them all (potential duplicates acceptable for analytics)
            if (typeof item !== 'object' && merged.includes(item)) {
              return; // Skip duplicates for primitive values
            }
            merged.push(item);
          });
          
          result[key] = merged;
        }
        // Recursively merge nested objects
        else if (
          typeof value === 'object' && 
          value !== null && 
          !Array.isArray(value) && 
          typeof result[key] === 'object' && 
          result[key] !== null && 
          !Array.isArray(result[key])
        ) {
          result[key] = this.mergeObjects(result[key], value);
        }
        // For primitive values or mismatched types, prefer the newer value
        else {
          result[key] = value;
        }
      });
    } catch (error) {
      // Removed complex console statement for security;
      // In case of merge error, make a best effort by using source as fallback
      return source;
    }
    
    return result;
  }
  
  /**
   * Standardizes naming convention by replacing 'Pro' with 'Plus'
   * Ensures consistent premium tier naming throughout the application
   * 
   * @param obj - The object to process for naming standardization
   */
  private standardizePlusNaming(obj: unknown): void {
    if (!obj || typeof obj !== 'object') return;
    
    if (Array.isArray(obj)) {
      // For arrays, process each item recursively
      obj.forEach(item => this.standardizePlusNaming(item));
      return;
    }
    
    const objRecord = obj as Record<string, unknown>;
    const keysToUpdate: string[] = [];
    
    // First pass: identify keys that need renaming
    Object.keys(objRecord).forEach(key => {
      const value = objRecord[key];
      
      // Process nested objects recursively
      if (value && typeof value === 'object') {
        this.standardizePlusNaming(value);
      }
      
      // Update string values containing 'Pro'
      if (typeof value === 'string' && value.includes('Pro')) {
        objRecord[key] = value.replace(/Pro/g, 'Plus');
      }
      
      // Identify keys that need to be renamed (containing 'pro')
      if (key.toLowerCase().includes('pro')) {
        keysToUpdate.push(key);
      }
    });
    
    // Second pass: rename keys (can't modify during iteration)
    keysToUpdate.forEach(key => {
      const newKey = key.replace(/pro/gi, 'plus');
      objRecord[newKey] = objRecord[key];
      delete objRecord[key];
    });
  }

  /**
   * Saves data to the database with enhanced error handling and exponential backoff
   * @param collection The collection name
   * @param id The document ID or an object
   * @param data The data to save
   * @returns A promise that resolves when the operation is complete
   */
  async saveData(collection: string, id: string | object, data: unknown): Promise<void> {
    // DEBUG: Log save operation details
    // Removed complex console statement for security;
    if (!collection) {
      
      throw new Error('Invalid collection');
    }

    try {
      await this.ensureInitialized();
    } catch (initError) {
      
      throw new Error(`Database initialization failed: ${initError.message || 'Unknown error'}`);
    }
    
    if (!this.db) {
      
      throw new Error('Database not initialized');
    }

    try {
      const docId = `${collection}:${typeof id === 'string' ? id : Date.now().toString()}`;
      let retries = 5; // Increase max retries
      const backoffTime = 100; // Start with 100ms backoff

      while (retries > 0) {
        try {
          // Try to get existing document first - handle potential conflicts by getting latest
          let existingDoc;
          try {
            // Always attempt to get the latest version with conflicts
            existingDoc = await this.db.get(docId, { conflicts: true });
            
            // If document has conflicts, resolve them first
            if (existingDoc._conflicts && existingDoc._conflicts.length > 0) {
              // Get all conflict versions
              const conflictDocs = await Promise.all(
                existingDoc._conflicts.map(rev => 
                  this.db.get(docId, { rev })
                )
              );
              
              // Manually resolve conflict by choosing the latest revision
              // and deleting the other conflict revisions
              await Promise.all(
                conflictDocs.map(conflictDoc => 
                  this.db.remove(conflictDoc._id, conflictDoc._rev)
                )
              );
              
              // Get the document again after resolving conflicts
              existingDoc = await this.db.get(docId);
            }
          } catch (err) {
            if (err.name === 'not_found') {
              existingDoc = { _id: docId };
            } else {
              // Only log non-analytics errors to reduce noise
              if (docId.indexOf('user_analytics') === -1) {
                
              }
              throw err;
            }
          }

          // Special case handling for analytics data which is prone to conflicts
          if (docId === 'user_analytics:main') {
            // Handle analytics update inline instead of using a separate method
            try {
              // Get the latest version of the document to ensure we have current revision
              const latestDoc = await this.db.get(docId) as PouchDocument;
              
              // Merge existing and new analytics data with special handling
              const existingData = latestDoc.data || {};
              const newData = data || {};
              
              // Create merged data by combining both objects
              const mergedData = this.mergeObjects(existingData, newData);
              
              // Ensure Plus tier naming (not Pro) throughout analytics data
              this.standardizePlusNaming(mergedData);
              
              // Prepare the updated document with proper revision
              const updatedDoc = {
                _id: latestDoc._id,
                _rev: latestDoc._rev,
                data: mergedData,
                timestamp: new Date().toISOString()
              };
              
              // Update the document
              await this.db.put(updatedDoc);
              
              // Update memory cache
              this.memoryCache.set(latestDoc._id, {
                data: mergedData,
                timestamp: Date.now(),
                ttl: this.CACHE_TTL
              });
              
              return;
            } catch (analyticsError) {
              if (analyticsError.name === 'conflict') {
                // For analytics conflicts, just discard silently to reduce console noise
                // No logging for analytics conflicts
                return;
              }
              // For other errors, let it fall through to the normal error handling
              throw analyticsError;
            }
          }

          // Prepare new document with revision if it exists
          const newDoc = {
            _id: docId,
            _rev: existingDoc._rev,
            data,
            timestamp: new Date().toISOString()
          };

          await this.db.put(newDoc);

          // Update memory cache
          this.memoryCache.set(docId, {
            data,
            timestamp: Date.now(),
            ttl: this.CACHE_TTL
          });

          return;
        } catch (err) {
          if (err.name === 'conflict' && retries > 1) {
            // Calculate exponential backoff delay with jitter to prevent thundering herd
            const baseDelay = Math.pow(2, 4 - retries) * 100; // 100ms, 200ms, 400ms, etc.
            const jitter = Math.random() * 200; // Increased jitter up to 200ms
            const delayMs = baseDelay + jitter;
            
            // Special handling for user_profiles to prevent duplication and resolve conflicts
            if (docId.startsWith('user_profiles:')) {
              try {
                // Get latest document version for smart merging with conflicts flag
                let latestDoc = await this.db.get(docId, { conflicts: true }) as PouchDocument & { _conflicts?: string[] };
                const existingData = latestDoc.data || {};
                const newData = data || {};
                
                // Check if there are conflicts and resolve them first
                if (latestDoc._conflicts && latestDoc._conflicts.length > 0) {
                  loggingService.warn('database', `Found ${latestDoc._conflicts.length} conflicts for ${docId}, resolving...`);
                  
                  try {
                    // Get all conflicting documents
                    const conflictDocs = await Promise.all(
                      latestDoc._conflicts.map(rev => 
                        this.db.get(docId, { rev })
                      )
                    );
                    
                    // Merge all conflicting documents data
                    let consolidatedData = typeof existingData === 'object' ? { ...(existingData as object) } : {};
                    for (const conflictDoc of conflictDocs) {
                      const docData = (conflictDoc as unknown as PouchDocument).data;
                      if (docData) {
                        consolidatedData = this.mergeObjects(consolidatedData, docData);
                      }
                    }
                    
                    // Delete all conflict revisions after merging their data
                    await Promise.all(
                      conflictDocs.map(doc => 
                        this.db.remove(doc._id, doc._rev)
                      )
                    );
                    
                    // Use merged data as our base
                    latestDoc = await this.db.get(docId) as PouchDocument;
                    latestDoc.data = consolidatedData;
                    
                    loggingService.info('database', `Successfully resolved ${conflictDocs.length} conflicts for ${docId}`);
                  } catch (conflictError) {
                    loggingService.error('database', `Failed to resolve conflicts: ${conflictError}`);
                    // Continue with existing document anyway
                  }
                }
                
                // Create merged profile data, preserving critical fields
                let mergedData = this.mergeObjects(existingData, newData);
                
                // Ensure Plus tier naming (not Pro) is consistent
                this.standardizePlusNaming(mergedData);
                
                // Add a more significant delay before trying to update the document
                // This helps prevent race conditions in conflict resolution by allowing
                // other concurrent operations to complete
                await new Promise(resolve => setTimeout(resolve, 800)); // Increased delay to 800ms
                
                // Get the latest version again to ensure we have the latest revision
                // Instead of verifying and possibly failing, we'll always get the latest
                try {
                  const freshDoc = await this.db.get(docId) as PouchDocument;
                  
                  // If the document changed during our processing, merge again with the latest
                  if (freshDoc._rev !== latestDoc._rev) {
                    loggingService.info('database', `Document ${docId} changed during processing, merging with latest version`);
                    
                    // Recursive re-merge with the latest data
                    const freshData = freshDoc.data || {};
                    mergedData = this.mergeObjects(freshData, mergedData);
                    latestDoc = freshDoc; // Update our reference to use the latest revision
                  }
                } catch (refetchError) {
                  loggingService.warn('database', `Failed to re-fetch latest document during conflict resolution: ${refetchError}`);
                  // Continue with our current data - it's still better than failing the operation
                }
                
                // Update the document with proper revision
                await this.db.put({
                  _id: latestDoc._id,
                  _rev: latestDoc._rev,
                  data: mergedData,
                  timestamp: new Date().toISOString()
                });
                
                // Update memory cache
                this.memoryCache.set(latestDoc._id, {
                  data: mergedData,
                  timestamp: Date.now(),
                  ttl: this.CACHE_TTL
                });
                
                loggingService.info('database', `Successfully resolved conflict for profile ${docId}`);
                return; // Exit the retry loop after successful merge
              } catch (mergeError) {
                // If merge strategy fails, fall back to normal retry
                loggingService.error('database', `Profile conflict resolution failed for ${docId}: ${mergeError instanceof Error ? mergeError.message : 'Unknown error'}`);
              }
            } 
            // Special handling for medications collection conflicts
            else if (collection === 'medications') {
              try {
                // Get the latest version of the document
                const latestMedicationDoc = await this.db.get(docId) as PouchDocument;
                
                // Create a new document with our updated data but the latest revision
                const updatedDoc = {
                  _id: docId,
                  _rev: latestMedicationDoc._rev,
                  data: data, // Use our current data (override conflicts)
                  timestamp: new Date().toISOString()
                };
                
                // Update the document with the correct revision
                await this.db.put(updatedDoc);
                
                // Update memory cache
                this.memoryCache.set(docId, {
                  data,
                  timestamp: Date.now(),
                  ttl: this.CACHE_TTL
                });
                
                loggingService.info('database', `Successfully overwrote medications doc for ${docId}`);
                return; // Exit the retry loop after successful overwrite
              } catch (overwriteError) {
                // Removed complex console statement for security;
              }
            }
            // Only log conflicts for non-analytics documents to reduce noise
            else if (docId.indexOf('user_analytics') === -1) {
              
            }
            
            retries--;
            await new Promise(resolve => setTimeout(resolve, delayMs));
            continue;
          }
          // Only log non-analytics errors to reduce noise
          if (docId.indexOf('user_analytics') === -1) {
            
          }
          throw err;
        }
      }
    } catch (error) {
      // Only log errors in development or for non-analytics collections
      if (process.env.NODE_ENV === 'development' || collection !== 'user_analytics') {
        // Removed complex console statement for security;
      }
      
      // Special handling for user_profiles collection - implement a fallback strategy for persistent conflicts
      if (error.name === 'conflict' && collection === 'user_profiles') {
        try {
          
          
          // First try to get the current document one more time
          try {
            // Get the current document with all conflicts
            const currentDoc = await this.db.get(`${collection}:${typeof id === 'string' ? id : ''}`, { conflicts: true }) as PouchDocument & { _conflicts?: string[] };
            
            // If we have conflicts, resolve them all first
            if (currentDoc._conflicts && currentDoc._conflicts.length > 0) {
              // Get all conflicting documents
              const conflictDocs = await Promise.all(
                currentDoc._conflicts.map(rev => 
                  this.db.get(currentDoc._id, { rev })
                )
              );
              
              // Delete all conflict revisions
              await Promise.all(
                conflictDocs.map(doc => {
                  // Type assertion for PouchDB document with proper fields
                  const pouchDoc = doc as unknown as { _id: string, _rev: string };
                  return this.db.remove(pouchDoc._id, pouchDoc._rev);
                })
              );
              
              
            }
            
            // Maximum retry - wait a full second
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Attempt final update with current rev
            await this.db.put({
              _id: currentDoc._id,
              _rev: currentDoc._rev,
              data: this.mergeObjects((currentDoc as unknown as PouchDocument).data || {}, data || {}),
              timestamp: new Date().toISOString(),
              resolved_conflict: true
            });
            
            
            return;
          } catch (finalResolutionError) {
            
          }
          
          // If all else fails, create an alternative document
          // Generate a timestamp-based suffix for the conflicted document ID
          const timestamp = Date.now();
          const idString = typeof id === 'string' ? id : 'unknown';
          const newDocId = `${collection}:${idString}_resolved_${timestamp}`;
          
          // Create a fresh document with the modified ID to avoid the conflict
          const newDoc = {
            _id: newDocId,
            data: data,
            timestamp: new Date().toISOString(),
            is_conflict_resolution: true,
            original_id: idString
          };
          
          // Save without revision checking
          await this.db.put(newDoc);
          
          // Cache the alternate version
          this.memoryCache.set(newDocId, {
            data,
            timestamp,
            ttl: this.CACHE_TTL
          });
          
          
          return; // Successfully created an alternative document
        } catch (fallbackError) {
          
        }
      }
      
      throw error;
    }
  }

  /**
   * Retrieves data from the database
   * @param collection - The collection to retrieve data from
   * @param id - Optional specific document ID to retrieve
   * @returns Promise resolving to the requested data or null if not found
   */
  async getData(collection: string, id?: string): Promise<unknown | null | Record<string, unknown[]>> {
    try {
      // Removed complex console statement for security;
      
      // Ensure database is initialized before proceeding
      if (!this.initialized) {
        try {
          await this.initialize();
        } catch (error) {
          const errorDetails = {
            name: error?.name || 'InitializationError',
            message: error instanceof Error ? error.message : 'Unknown initialization error',
            stack: process.env.NODE_ENV !== 'production' ? (error instanceof Error ? error.stack : undefined) : undefined
          };
          
          // Return an empty result instead of throwing to prevent cascading errors
          return collection === 'medications' ? {} : (id ? null : []);
        }
      }
    } catch (logError) {
      // Catch any errors in the logging or initialization check itself
      
      // Continue with operation even if logging fails
    }
    
    if (!this.db) {
      
      return collection === 'medications' ? {} : (id ? null : []);
    }
    
    // For medication collection with profile IDs, initialize if not found
    if (collection === 'medications' && id && (id === 'u1' || id === 'u2')) {
      try {
        // Check if this medication profile exists
        await this.db.get(`${collection}:${id}`);
      } catch (error) {
        if (error.status === 404 && error.name === 'not_found') {
          // Create an empty medication document for this profile to prevent future 404s
          const emptyDoc = {
            _id: `${collection}:${id}`,
            data: {},
            timestamp: new Date().toISOString(),
            created: true
          };
          
          try {
            await this.db.put(emptyDoc);
            
            return {};
          } catch (initError) {
            
            // Continue with normal flow, we tried our best to initialize
          }
        }
      }
    }
    // Initialize properly structured error object
    let lastError: Record<string, any> = {
      name: 'DatabaseAccessError',
      message: `Error accessing ${collection} data`,
      timestamp: new Date().toISOString(),
      collection,
      attempts: 0
    };
    
    const retries = 3;
    for (let i = 0; i < retries; i++) {
      try {
        if (id) {
          const docId = `${collection}:${id}`;
          
          // Check cache first
          const cached = this.memoryCache.get(docId);
          if (cached && this.isValidCacheItem(cached)) {
            return cached.data;
          }
          
          try {
            // Cast PouchDB document to the expected type structure with data property
            const doc = await this.db.get(docId) as PouchDocument;
            const data = doc.data;

            // Update cache
            this.memoryCache.set(docId, {
              data,
              timestamp: Date.now(),
              ttl: this.CACHE_TTL
            });

            return data;
          } catch (error) {
            // Handle not_found errors gracefully
            if (error && error.status === 404 && error.name === 'not_found') {
              // Don't log these repeatedly for medications - they're expected on first run
              if (!(collection === 'medications' && (id === 'u1' || id === 'u2'))) {
                
              }
              
              // For medications, return empty object with proper structure
              if (collection === 'medications') {
                return {};
              }
              
              return null;
            }
            
            // Enhance error object before throwing
            const enhancedError = {
              ...error,
              name: error?.name || 'DatabaseError',
              message: error?.message || `Error retrieving ${collection} data`,
              status: error?.status || 500,
              docId: docId,
              collection: collection,
              timestamp: new Date().toISOString(),
              stack: process.env.NODE_ENV !== 'production' ? error?.stack : undefined
            };
            
            
            throw enhancedError; // Throw the enhanced error
          }
        } else {
          // Retrieve all docs for collection
          const result = await this.db.allDocs({
            include_docs: true,
            startkey: `${collection}:`,
            endkey: `${collection}:\ufff0`
          });

          if (collection === 'medications') {
            const medications = result.rows
              .map(row => {
                // Access the data property safely with a more complete type assertion
                const doc = ((row.doc as PouchDocument)?.data as Record<string, unknown>) || {};
                if (!doc) {
                  return null;
                }

                // Ensure minimum required fields
                const medication = {
                  id: doc.id || row.id.split(':')[1] || `med-${Date.now()}`,
                  name: doc.name || 'Unnamed Medication',
                  dosage: doc.dosage || 'No dosage specified',
                  instructions: doc.instructions || '',
                  timing: doc.timing || 'morning',
                  time: doc.timing || 'morning',
                  taken: Boolean(doc.taken),
                  withFood: Boolean(doc.withFood),
                  cycle: doc.cycle || 'daily',
                  duration: doc.duration || null,
                  category: doc.category || 'supplement',
                  // Add frequency property for the Plus tier feature
                  frequency: doc.frequency || { pattern: 'daily' },
                  // Also include strictTiming for Plus tier
                  strictTiming: Boolean(doc.strictTiming)
                };

                return medication;
              })
              .filter(Boolean);
            
            // Clear cache to ensure fresh data
            this.memoryCache.clear();

            const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const medicationsByDay = daysOfWeek.reduce((acc, day) => {
              // Initialize with an empty array for each day
              acc[day] = [];
              return acc;
            }, {} as Record<string, typeof medications>);
            
            // Properly assign medications to the days they should be taken
            medications.forEach(medication => {
              // Type guard for medication frequency
              interface FrequencyWeekly {
                pattern: 'weekly';
                weekDay: string;
              }
              
              interface FrequencySpecificDays {
                pattern: 'specificDays';
                days: string[];
              }
              
              interface FrequencyDaily {
                pattern: 'daily';
              }
              
              type Frequency = FrequencyWeekly | FrequencySpecificDays | FrequencyDaily;
              
              // Type guard for frequency patterns
              const isWeeklyFrequency = (freq: unknown): freq is FrequencyWeekly => {
                if (typeof freq !== 'object' || freq === null) return false;
                
                const freqObj = freq as Record<string, unknown>;
                return 'pattern' in freqObj && 
                       freqObj.pattern === 'weekly' &&
                       'weekDay' in freqObj &&
                       typeof freqObj.weekDay === 'string';
              };
                
              const isSpecificDaysFrequency = (freq: unknown): freq is FrequencySpecificDays => {
                if (typeof freq !== 'object' || freq === null) return false;
                
                const freqObj = freq as Record<string, unknown>;
                return 'pattern' in freqObj && 
                       freqObj.pattern === 'specificDays' &&
                       'days' in freqObj && 
                       Array.isArray(freqObj.days);
              };
              
              // Check the frequency pattern and handle accordingly
              const frequency = medication.frequency as unknown;
              
              if (isWeeklyFrequency(frequency)) {
                // For weekly medications, only add to the specified day
                const weekDay = frequency.weekDay;
                // Ensure proper capitalization of the day name
                const properDayName = daysOfWeek.find(day => day.toLowerCase() === weekDay.toLowerCase());
                if (properDayName) {
                  medicationsByDay[properDayName].push(medication);
                }
              } else if (isSpecificDaysFrequency(frequency)) {
                // For medications taken on specific days
                frequency.days.forEach(specificDay => {
                  const properDayName = daysOfWeek.find(day => day.toLowerCase() === specificDay.toLowerCase());
                  if (properDayName) {
                    medicationsByDay[properDayName].push(medication);
                  }
                });
              } else {
                // Default: daily medications go in every day
                daysOfWeek.forEach(day => {
                  medicationsByDay[day].push(medication);
                });
              }
            });
            
            return medicationsByDay;
          }

          const processedData = result.rows.map(row => (row.doc as PouchDocument)?.data).filter(Boolean);
          return processedData;
        }
      } catch (error) {
        
        lastError = error;
        if (error.name === 'not_found') {
          return null;
        }
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, i)));
          continue;
        }
        throw lastError;
      }
    }
    throw lastError;
  }

  async clearAllData(): Promise<void> {
    if (!this.db) return;
    try {
      await this.db.destroy();
      this.db = null;
      this.initialized = false;
      this.memoryCache.clear();
      // Reinitialize the database after clearing
      await this.initialize();
    } catch (error) {
      
      throw error;
    }
  }

  /**
   * Ensures the database is initialized before performing operations
   * @returns Promise that resolves when initialization is complete
   */
  public async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
  }

  // Using isInitialized method defined at the top of the class
  
  async deleteMedication(medicationName: string): Promise<void> {
    await this.ensureInitialized();
    if (!this.db) throw new Error('Database not initialized');
    
    try {
      // Get all documents to find medication entries
      const result = await this.db.allDocs({
        include_docs: true,
        startkey: 'medications:',
        endkey: 'medications:\ufff0'
      });
      
      // Find documents containing the medication name
      const docsToDelete = result.rows.filter(row => {
        const docData = ((row.doc as PouchDocument)?.data as Record<string, unknown> | undefined);
        return docData?.name === medicationName;
      });
      
      // Delete each matching document
      for (const doc of docsToDelete) {
        await this.db.remove(doc.doc);
      }
      
      // Clear cache to ensure fresh data
      this.memoryCache.clear();
    } catch (error) {
      
      throw error;
    }
  }
  
  async getAllRecords(): Promise<PouchDocument[]> {
    await this.ensureInitialized();
    if (!this.db) throw new Error('Database not initialized');

    try {
      const result = await this.db.allDocs({
        include_docs: true
      });
      return result.rows.map(row => row.doc as PouchDocument);
    } catch (error) {
      
      return [];
    }
  }
  
  /**
   * Delete a document by collection and ID
   * @param collection - The collection name
   * @param id - The ID of the document to delete
   * @param maxRetries - Maximum number of retry attempts (default: 3)
   * @returns Promise that resolves when deletion is complete
   */
  async deleteDataById(collection: string, id: string, maxRetries = 3): Promise<void> {
    await this.ensureInitialized();
    if (!this.db) throw new Error('Database not initialized');
    
    let retries = maxRetries;
    const docId = `${collection}:${id}`;
    
    while (retries > 0) {
      try {
        // Get the document first to get its revision
        const doc = await this.db.get(docId);
        
        // Delete the document with its revision
        await this.db.remove(doc);
        
        // Remove from cache if present
        this.memoryCache.delete(docId);
        
        return; // Success, exit the retry loop
        
      } catch (error) {
        if (error.name === 'not_found') {
          return; // Not an error if document doesn't exist
        }
        
        if (error.name === 'conflict' && retries > 1) {
          // Conflict detected, retry with exponential backoff
          retries--;
          const delayMs = Math.pow(2, maxRetries - retries) * 100;
          
          await new Promise(resolve => setTimeout(resolve, delayMs));
          continue;
        }
        
        
        throw error;
      }
    }
    
    // If we get here, we've exhausted our retries
    
    throw new Error(`Failed to delete document after ${maxRetries} attempts due to conflicts`);
  }

  public async ensureUserAnalyticsDocument(): Promise<void> {
    try {
      const doc = await this.db?.get('user_analytics:main');
      // Document exists, no action needed
    } catch (error) {
      if (error.name === 'not_found') {
        // Document does not exist, create it
        await this.db?.put({
          _id: 'user_analytics:main',
          data: {},
          timestamp: new Date().toISOString(),
        });
      } else {
        
        throw error;
      }
    }
  }
}

export const databaseService = new DatabaseService();